import { useOrganization } from '@/contexts/OrganizationContext'

/**
 * May the current organization use the shared Sage Intacct connection? (DCP-368)
 *
 * The credentials are platform-wide env vars, so before this flag existed every
 * tenant rendered as "connected" — including one created for a different
 * client, whose syncs then pulled another client's Sage company into its
 * schema. `organizations.sage_intacct_enabled` is what the backend gates on;
 * this hook is the same question asked client-side.
 *
 * NOT a security boundary and not pretending to be one. The gate that matters
 * lives in the `sage-sync` edge function (403) and in every Sage task, which
 * refuses before a credential is loaded. This exists so the UI stops OFFERING
 * an integration the tenant does not have, rather than letting the user click
 * into an error.
 *
 * One hook rather than the check written at each screen: `/settings/sage` and
 * the Intacct Balances tab are two entrances to the same syncs, and a rule
 * spelled out twice is a rule that eventually disagrees with itself.
 *
 * @returns `isEnabled` — false until the organization is known to be enabled,
 * so a not-yet-loaded organization renders as not-connected rather than
 * flashing the integration and taking it away; `isResolved` — whether the
 * organization has loaded, i.e. whether a false `isEnabled` is the answer or
 * only the default (lets a screen show a placeholder instead of hiding Sage
 * content it may be about to show); `organizationName` for the message.
 */
export function useSageAccess(): {
  isEnabled: boolean
  isResolved: boolean
  organizationName: string | null
} {
  const { currentOrganization } = useOrganization()

  return {
    isResolved: currentOrganization != null,
    // `=== true` rather than truthiness: the column is NOT NULL, but the org
    // record is JSON off the wire and a legacy/partial row must not read as
    // permission.
    isEnabled: currentOrganization?.sage_intacct_enabled === true,
    organizationName: currentOrganization?.name ?? null,
  }
}
